import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "../../../utils/toast";
import { Button, Input, Modal, Select } from "../../ui";
import { errorMessageOf, fieldErrorsOf, problemOf, scholarshipsApi } from "../../../api/scholarships";
import { COUNTRY_OPTIONS } from "../model";
import { SPONSORS_KEY } from "./model";

const EMPTY = { name: "", countryCode: "" };

// A new sponsor organisation; its staff are added as members afterwards
export default function NewSponsorDialog({ open, onClose, onCreated }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const close = () => {
    setForm(EMPTY);
    setErrors({});
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = {};
    if (!form.name.trim()) found.name = "Name is required";
    if (!form.countryCode) found.countryCode = "Country is required";
    if (Object.keys(found).length) return setErrors(found);
    setSaving(true);
    try {
      const { data } = await scholarshipsApi.post("admin/sponsors", { name: form.name.trim(), countryCode: form.countryCode });
      queryClient.invalidateQueries({ queryKey: SPONSORS_KEY });
      toast.success(`${data.name} added`);
      onCreated?.(data);
      close();
    } catch (error) {
      console.error("Failed to create a partner:", error);
      const problem = problemOf(error);
      setErrors(problem?.code === "sponsor-exists" ? { name: "A partner with this name already exists" } : fieldErrorsOf(problem));
      toast.error(errorMessageOf(error, "Failed to add the partner. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const field = (name) => ({
    value: form[name],
    onChange: (e) => {
      setErrors({});
      setForm((prev) => ({ ...prev, [name]: e.target.value }));
    },
    error: errors[name],
    disabled: saving,
  });

  return (
    <Modal
      open={open}
      onClose={close}
      title="New partner"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="new-sponsor" loading={saving}>
            Add partner
          </Button>
        </>
      }
    >
      <form id="new-sponsor" onSubmit={submit} className="grid gap-4">
        <Input label="Name" required maxLength={200} placeholder="Korea International Cooperation Agency (KOICA)" {...field("name")} />
        <Select label="Country" required placeholder="Select the country" options={COUNTRY_OPTIONS} {...field("countryCode")} />
      </form>
    </Modal>
  );
}
